import {useCallback, useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {Combobox, type ComboboxOption} from '@/components/ui/combobox'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import {DialogForm} from '@/components/shared/DialogForm'
import {FormField} from '@/components/shared/FormField'
import {SaveCancelFooter} from '@/components/shared/SaveCancelFooter'
import {InfoBanner} from '@/components/shared/InfoBanner'
import {ApiError} from '@/api/client'
import {useAssignServiceAccountRole} from '@/features/service-accounts/hooks/useServiceAccountRoles'
import type {Role} from '@/types/roles'

interface AssignRoleDialogProps {
    serviceAccountId: string
    excludeRoleIds: ReadonlyArray<string>
    open: boolean
    onOpenChange: (next: boolean) => void
}

export function AssignRoleDialog({
    serviceAccountId,
    excludeRoleIds,
    open,
    onOpenChange,
}: AssignRoleDialogProps) {
    const i18n = useI18n()
    const services = useServices()

    const [search, setSearch] = useState('')
    const [selectedRoleId, setSelectedRoleId] = useState<string | null>(null)
    const [inlineError, setInlineError] = useState<string | null>(null)

    const rolesQuery = useQuery({
        queryKey: ['service-account-assign-role-search', search],
        queryFn: () => services.roles.list({q: search || undefined, size: 50}),
        enabled: open,
    })

    const assignMutation = useAssignServiceAccountRole(serviceAccountId)

    const excluded = useMemo(() => new Set(excludeRoleIds), [excludeRoleIds])

    const availableRoles: Role[] = useMemo(() => {
        const content = rolesQuery.data?.content ?? []
        return content.filter((r: Role) => !excluded.has(r.id))
    }, [rolesQuery.data, excluded])

    const options: ComboboxOption[] = useMemo(() =>
        availableRoles.map((r) => ({
            value: r.id,
            label: r.name,
            description: r.description ?? undefined,
        })),
    [availableRoles])

    const selectedRole = availableRoles.find((r) => r.id === selectedRoleId) ?? null

    const handleOpenChange = useCallback((next: boolean) => {
        if (!next) {
            setSearch('')
            setSelectedRoleId(null)
            setInlineError(null)
        }
        onOpenChange(next)
    }, [onOpenChange])

    const handleSubmit = useCallback((e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault()
        if (!selectedRoleId) return
        setInlineError(null)
        assignMutation.mutate(
            {roleId: selectedRoleId},
            {
                onSuccess: () => handleOpenChange(false),
                onError: (err) => {
                    if (err instanceof ApiError && err.status === 409) {
                        setInlineError(i18n.serviceAccounts.roleAlreadyAssignedError as string)
                    } else {
                        setInlineError((err as Error).message)
                    }
                },
            },
        )
    }, [selectedRoleId, assignMutation, handleOpenChange, i18n.serviceAccounts.roleAlreadyAssignedError])

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogContent>
                <DialogHeader>
                    <DialogTitle data-testid="assign-role-dialog-title">
                        {i18n.serviceAccounts.assignRoleTitle}
                    </DialogTitle>
                    <DialogDescription>
                        {i18n.serviceAccounts.assignRoleDescription}
                    </DialogDescription>
                </DialogHeader>
                <DialogForm onSubmit={handleSubmit} data-testid="assign-role-form">
                    {inlineError && (
                        <InfoBanner variant="destructive" data-testid="assign-role-inline-error">
                            {inlineError}
                        </InfoBanner>
                    )}
                    <FormField
                        label={i18n.serviceAccounts.roleLabel as string}
                        htmlFor="assign-role-combobox"
                        required
                    >
                        <Combobox
                            options={options}
                            value={selectedRoleId}
                            onValueChange={(v) => setSelectedRoleId(v ?? null)}
                            onSearch={setSearch}
                            placeholder={i18n.serviceAccounts.selectRolePlaceholder as string}
                            emptyMessage={i18n.serviceAccounts.noRolesFound as string}
                            isLoading={rolesQuery.isLoading}
                            selectedLabel={selectedRole?.name}
                            data-testid="assign-role-combobox"
                        />
                    </FormField>
                    <SaveCancelFooter
                        testIdPrefix="assign-role"
                        onCancel={() => handleOpenChange(false)}
                        saving={assignMutation.isPending}
                        saveLabel={i18n.serviceAccounts.assignButton as string}
                        disabled={!selectedRoleId}
                    />
                </DialogForm>
            </DialogContent>
        </Dialog>
    )
}
